import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    TextField,
    IconButton,
    Grid,
    Typography,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    Snackbar,
    Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import InputMask from 'react-input-mask';
import styled from '@emotion/styled';
import flaskServer from '../config/flaskserver';
import StyledButton from '../layout/StyledButton';

const FormContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    paddingTop: '64px',
});

const Form = styled('div')({
    width: '100%',
    maxWidth: '500px',
    textAlign: 'center',
    padding: '32px',
    borderRadius: '8px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
});

const FormInput = styled(TextField)({
    margin: '8px',
    width: '100%',
});

const PerfilUsuario = ({ handleLogout }) => {
    const [userInfo, setUserInfo] = useState({
        username: '',
        nomeEmpresa: '',
        celular: '',
        email: '',
        isAdminUser: false,
        num_accesses_allowed: '', // Número de acessos simultâneos permitidos
        validade: '', // Data de validade
    });

    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    useEffect(() => {
        const fetchUserInfo = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await axios.get(`${flaskServer.flaskServerIP}/user-profile`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                
                if (response.status === 401) {
                    handleLogout();
                    return;
                }

                setUserInfo(response.data.user);
            } catch (error) {
                
                handleLogout();
            }
        };

        fetchUserInfo();
    }, [handleLogout]);

    const handleUpdate = async () => {
        const token = localStorage.getItem('token');
        try {
            if (!token) {
                showSnackbar('Token não encontrado. Faça login novamente.', 'error');
                handleLogout();
                return;
            }

            const response = await axios.put(`${flaskServer.flaskServerIP}/update-profile`, {
                ...userInfo,
                password,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                handleLogout();
                return;
            }

            showSnackbar('Perfil atualizado com sucesso!', 'success');
        } catch (error) {
            
            showSnackbar('Erro ao atualizar perfil.', 'error');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserInfo((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handlePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleUpdatePassword = async () => {
        if (newPassword !== confirmPassword) {
            showSnackbar('As senhas não correspondem. Tente novamente.', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${flaskServer.flaskServerIP}/update-password`, {
                newPassword,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                handleLogout();
                return;
            }

            showSnackbar('Senha atualizada com sucesso!', 'success');
            setOpenDialog(false);
        } catch (error) {
            
            showSnackbar('Erro ao atualizar senha.', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const closeSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <FormContainer>
            <Form>
                <Typography variant="h5" gutterBottom>
                    Editar Perfil
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label="Seu Nome"
                            name="username"
                            value={userInfo.username}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label="Nome da Empresa"
                            name="nomeEmpresa"
                            value={userInfo.nomeEmpresa}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputMask
                            mask="(99) 99999-9999"
                            value={userInfo.celular}
                            onChange={handleChange}
                            name="celular"
                        >
                            {(inputProps) => (
                                <FormInput
                                    {...inputProps}
                                    label="Celular"
                                    required
                                />
                            )}
                        </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label="E-mail"
                            name="email"
                            value={userInfo.email}
                            onChange={handleChange}
                            type="email"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label="Validade"
                            name="validade"
                            type="date"
                            value={userInfo.validade}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ disabled: true }} // Torna o campo inativo
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormInput
                            label="Número de acessos simultâneos permitidos"
                            type="number"
                            name="num_accesses_allowed"
                            value={userInfo.num_accesses_allowed}
                            InputProps={{ disabled: true }} // Torna o campo inativo
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <StyledButton
                            variant="contained"
                            color="secondary"
                            onClick={handleOpenDialog}
                        >
                            Alterar Senha
                        </StyledButton>
                    </Grid>
                </Grid>
                <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={handleUpdate}
                >
                    Atualizar
                </StyledButton>
            </Form>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Alterar Senha</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Digite e confirme sua nova senha.
                    </DialogContentText>
                    <FormInput
                        autoFocus
                        margin="dense"
                        label="Nova Senha"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handlePasswordVisibility}>
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormInput
                        margin="dense"
                        label="Confirme a Senha"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handlePasswordVisibility}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <StyledButton color="primary" onClick={handleCloseDialog}>
                        Cancelar
                    </StyledButton>
                    <StyledButton color="primary" onClick={handleUpdatePassword}>
                        Salvar
                    </StyledButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={closeSnackbar}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </FormContainer>
    );
};

export default PerfilUsuario;

